/**
 * Blog voice hire handoff (CEL-3260; contract agent_docs/blog_voice/BLOG_VOICE_CONTRACT.md §3 "Start this", §6).
 *
 * A blog call ends with a brief; "Start this" navigates to
 *   /ai_employee?role=<role slug>&brief=<tasks, newline-joined>&src=blog-voice&vs=<session id>
 * The hire page is a protected route, so a signed-out reader detours through login/signup, email verification and consent,
 * none of which keep the query string for a NEW account (consent always lands on "/"). The carry is the planCarry pattern:
 *   1. blogVoiceCapture.captureBlogVoiceHire() runs at app boot (index.js, beside captureFirstTouch): on the hire path with
 *      src=blog-voice it stashes {role, tasks, vs} here (localStorage survives the Google redirect, the verification tab and
 *      consent) and fires the start-this stamp once per browser session.
 *   2. RootRoute sends a joined account that still holds the stash from "/" to /ai_employee (hasBlogVoiceHire).
 *   3. NewEmployeePage seeds the form from the URL, else the stash (readBlogVoiceSeed), and consumes the stash.
 * This module is storage only (no network import), so RootRoute can read it without pulling the API client into its chunk.
 * `vs` itself rides the first-touch record (firstTouch.js) to signup. Last write wins; the TTL bounds staleness.
 */
import { normalizeBlogVoiceSessionId } from './firstTouch';

const HIRE_KEY = 'cellcog_blog_voice_hire';
const HIRE_TTL_MS = 24 * 60 * 60 * 1000; // 24h, the planCarry window
export const BLOG_VOICE_SRC = 'blog-voice';
export const HIRE_PATH = '/ai_employee';
const ROLE_SLUG_RE = /^[a-z0-9-]{1,64}$/;
const MAX_TASKS = 3;
const MAX_TASK_CHARS = 200;

export const isHirePath = (pathname) => pathname === HIRE_PATH || pathname === `${HIRE_PATH}/`;

const cleanTasks = (raw) =>
  String(raw || '')
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((t) => t.trim().slice(0, MAX_TASK_CHARS))
    .filter(Boolean)
    .slice(0, MAX_TASKS);

/** {role, tasks, vs} from a query string carrying src=blog-voice, a role slug and at least one task; else null. */
export function parseBlogVoiceHire(search) {
  try {
    const params = new URLSearchParams(search || '');
    if (params.get('src') !== BLOG_VOICE_SRC) return null;
    const role = (params.get('role') || '').trim().toLowerCase();
    const tasks = cleanTasks(params.get('brief'));
    if (!ROLE_SLUG_RE.test(role) || tasks.length === 0) return null;
    return { role, tasks, vs: normalizeBlogVoiceSessionId(params.get('vs')) };
  } catch (e) {
    return null;
  }
}

/** Write the stash (boot capture). Fail-soft. */
export function stashBlogVoiceHire(hire) {
  try {
    if (hire) localStorage.setItem(HIRE_KEY, JSON.stringify({ ...hire, captured_at: Date.now() }));
  } catch (e) {
    // fail-soft
  }
}

/** Non-destructive read of a valid, unexpired stash, or null. */
export function peekBlogVoiceHire() {
  try {
    const raw = localStorage.getItem(HIRE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s || !s.captured_at || Date.now() - s.captured_at > HIRE_TTL_MS) return null;
    if (!ROLE_SLUG_RE.test(String(s.role || '')) || !Array.isArray(s.tasks)) return null;
    const tasks = cleanTasks(s.tasks.join('\n'));
    return tasks.length ? { role: s.role, tasks, vs: normalizeBlogVoiceSessionId(s.vs) } : null;
  } catch (e) {
    return null;
  }
}

export const hasBlogVoiceHire = () => !!peekBlogVoiceHire();

/** Remove the stash (the hire page has read it). */
export function consumeBlogVoiceHire() {
  try {
    localStorage.removeItem(HIRE_KEY);
  } catch (e) {
    // fail-soft
  }
}

/** The hire page's seed: the URL first (the reader is signed in), else the stash (after a signup detour). */
export function readBlogVoiceSeed(search) {
  return parseBlogVoiceHire(search) || peekBlogVoiceHire();
}

/** The brief the form starts with: the role line, then the tasks. Plain text (markdown markers would show literally). */
export function buildHireBrief(roleDisplay, tasks) {
  const name = (roleDisplay || '').trim();
  const word = name.split(/\s+/)[0] || '';
  const article = /^[A-Z]{2,}$/.test(word)
    ? ('AEFHILMNORSX'.includes(word[0]) ? 'an' : 'a')
    : (/^[aeiou]/i.test(word) ? 'an' : 'a');
  const head = name ? `Hire ${article} ${name}` : 'Hire an AI employee';
  const list = (tasks || []).map((t, i) => `${i + 1}. ${t}`).join('\n');
  const label = (tasks || []).length === 3 ? 'First three tasks:' : 'First tasks:';
  return `${head}\n\n${label}\n${list}`;
}
